import React from 'react';
import type { AdminContentItem, AdminContentKind } from '../contentConsoleApi';
import ConsolePagination from './ConsolePagination';
import ImageCropDialog from './ImageCropDialog';
import {
  createCooperationItemForm,
  createRequirementForm,
  LOCALES,
  NEWS_TOPICS,
  normalizeCooperationIndices,
  normalizeRequirementIndices,
  RESOURCE_CATEGORIES,
  SERVICE_PAGE_SLOT_OPTIONS,
  type ConsoleLocale,
  type ContentForm,
} from './models';

export type ContentWorkspaceProps = {
  kind: AdminContentKind;
  keyword: string;
  items: AdminContentItem[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
  selectedId: number | null;
  content: ContentForm;
  locale: ConsoleLocale;
  loading: boolean;
  loadingItems: boolean;
  saving: boolean;
  previewUrl: string;
  onKeywordChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onSelectItem: (id: number) => Promise<void>;
  onCreateNew: () => void;
  onDelete: () => Promise<void>;
  onSave: () => Promise<void>;
  setContent: React.Dispatch<React.SetStateAction<ContentForm>>;
  setLocale: (locale: ConsoleLocale) => void;
};

const KIND_LABELS: Record<AdminContentKind, string> = {
  news: '新闻管理',
  resource: '资源管理',
  career: '招聘管理',
  'project-cooperation': '项目合作管理',
};

const KIND_PANEL_TAGS: Record<AdminContentKind, string> = {
  news: 'NEWS',
  resource: 'RESOURCE',
  career: 'CAREER',
  'project-cooperation': 'COOPERATION',
};

const KIND_DESCRIPTIONS: Record<AdminContentKind, string> = {
  news: '用于维护新闻内容、分组关联及置顶展示顺序。',
  resource: '用于维护资源内容、分类、置顶及服务案例展示位。',
  career: '用于维护招聘信息、默认展示职位及置顶顺序。',
  'project-cooperation': '用于维护项目合作页面内容、合作卡片和问卷二维码。',
};

const KIND_CREATE_LABELS: Record<AdminContentKind, string> = {
  news: '新建新闻版本',
  resource: '新建资源版本',
  career: '新建招聘版本',
  'project-cooperation': '新建项目合作版本',
};

const LANGUAGE_LABELS: Record<ConsoleLocale, string> = {
  zh: '中文',
  en: 'English',
  ja: '日文',
};

const ContentWorkspace: React.FC<ContentWorkspaceProps> = ({
  kind,
  keyword,
  items,
  totalItems,
  currentPage,
  totalPages,
  selectedId,
  content,
  locale,
  loading,
  loadingItems,
  saving,
  previewUrl,
  onKeywordChange,
  onPageChange,
  onSelectItem,
  onCreateNew,
  onDelete,
  onSave,
  setContent,
  setLocale,
}) => {
  const [editorView, setEditorView] = React.useState<'basic' | 'requirements' | 'body'>('basic');
  const [cropTarget, setCropTarget] = React.useState<'cover' | 'qr' | null>(null);
  const [cropFile, setCropFile] = React.useState<File | null>(null);

  React.useEffect(() => {
    setEditorView('basic');
  }, [kind, content.id, locale]);

  const usesQrImage = kind === 'career' || kind === 'project-cooperation';
  const mediaLabel = usesQrImage ? '二维码图片' : '封面图片';

  const openCropper = (target: 'cover' | 'qr', file: File | null, input: HTMLInputElement) => {
    if (!file) return;
    setCropTarget(target);
    setCropFile(file);
    input.value = '';
  };

  const closeCropper = () => {
    setCropTarget(null);
    setCropFile(null);
  };

  const applyCroppedFile = (file: File) => {
    if (cropTarget === 'qr') {
      setContent((current) => ({ ...current, qrImageFile: file, clear_qr_image: false }));
    } else if (cropTarget === 'cover') {
      setContent((current) => ({ ...current, coverFile: file, clear_cover_image: false }));
    }
    closeCropper();
  };

  const updateRequirement = (requirementIndex: number, field: 'index' | 'title' | 'description', value: string) => {
    setContent((current) => ({
      ...current,
      requirements: normalizeRequirementIndices(
        current.requirements.map((requirement, index) => (
          index === requirementIndex ? { ...requirement, [field]: value } : requirement
        )),
      ),
    }));
  };

  const addRequirement = () => {
    setContent((current) => ({
      ...current,
      requirements: [...normalizeRequirementIndices(current.requirements), createRequirementForm(current.requirements.length + 1)],
    }));
  };

  const removeRequirement = (requirementIndex: number) => {
    setContent((current) => ({
      ...current,
      requirements: normalizeRequirementIndices(current.requirements.filter((_, index) => index !== requirementIndex)),
    }));
  };

  const updateCooperationItem = (itemIndex: number, field: 'index' | 'title' | 'description', value: string) => {
    setContent((current) => ({
      ...current,
      cooperation_items: normalizeCooperationIndices(
        current.cooperation_items.map((item, index) => (
          index === itemIndex ? { ...item, [field]: value } : item
        )),
      ),
    }));
  };

  const addCooperationItem = () => {
    setContent((current) => ({
      ...current,
      cooperation_items: [...normalizeCooperationIndices(current.cooperation_items), createCooperationItemForm(current.cooperation_items.length + 1)],
    }));
  };

  const removeCooperationItem = (itemIndex: number) => {
    setContent((current) => ({
      ...current,
      cooperation_items: normalizeCooperationIndices(current.cooperation_items.filter((_, index) => index !== itemIndex)),
    }));
  };

  const renderCareerRequirements = () => (
    <section className="content-console-section-card">
      <div className="content-console-section-head is-inline-head">
        <h3>岗位要求</h3>
        <p>请在此维护岗位要求内容。</p>
      </div>
      <div className="content-console-requirement-stack">
        {content.requirements.map((requirement, index) => (
          <article key={`${requirement.index}-${index}`} className="content-console-requirement-card">
            <div className="content-console-requirement-card-head">
              <strong>要求 {requirement.index || String(index + 1).padStart(2, '0')}</strong>
              <button type="button" className="content-console-secondary-button" onClick={() => removeRequirement(index)}>删除</button>
            </div>
            <div className="content-console-form-grid is-two-column">
              <label>
                <span>序号</span>
                <input value={requirement.index} onChange={(event) => updateRequirement(index, 'index', event.target.value)} />
              </label>
              <div className="content-console-requirement-placeholder" />
            </div>
            <div className="content-console-form-grid is-single-column">
              <label>
                <span>标题</span>
                <input value={requirement.title} onChange={(event) => updateRequirement(index, 'title', event.target.value)} />
              </label>
              <label>
                <span>描述</span>
                <textarea rows={3} value={requirement.description} onChange={(event) => updateRequirement(index, 'description', event.target.value)} />
              </label>
            </div>
          </article>
        ))}
      </div>
      <div className="content-console-action-row is-spaced-top">
        <button type="button" className="content-console-secondary-button" onClick={addRequirement}>新增要求</button>
      </div>
    </section>
  );

  const renderCooperationItems = () => (
    <section className="content-console-section-card">
      <div className="content-console-section-head is-inline-head">
        <h3>合作内容卡片</h3>
        <p>请在此维护项目合作页面中的合作模块内容。</p>
      </div>
      <div className="content-console-requirement-stack">
        {content.cooperation_items.map((item, index) => (
          <article key={`${item.index}-${index}`} className="content-console-requirement-card">
            <div className="content-console-requirement-card-head">
              <strong>模块 {item.index || String(index + 1).padStart(2, '0')}</strong>
              <button type="button" className="content-console-secondary-button" onClick={() => removeCooperationItem(index)}>删除</button>
            </div>
            <div className="content-console-form-grid is-two-column">
              <label>
                <span>序号</span>
                <input value={item.index} onChange={(event) => updateCooperationItem(index, 'index', event.target.value)} />
              </label>
              <div className="content-console-requirement-placeholder" />
            </div>
            <div className="content-console-form-grid is-single-column">
              <label>
                <span>标题</span>
                <input value={item.title} onChange={(event) => updateCooperationItem(index, 'title', event.target.value)} />
              </label>
              <label>
                <span>描述</span>
                <textarea rows={3} value={item.description} onChange={(event) => updateCooperationItem(index, 'description', event.target.value)} />
              </label>
            </div>
          </article>
        ))}
      </div>
      <div className="content-console-action-row is-spaced-top">
        <button type="button" className="content-console-secondary-button" onClick={addCooperationItem}>新增合作内容</button>
      </div>
    </section>
  );

  return (
    <div className="content-console-layout">
      <ImageCropDialog
        open={cropTarget !== null}
        file={cropFile}
        target={cropTarget ?? 'cover'}
        onCancel={closeCropper}
        onConfirm={applyCroppedFile}
      />

      <aside className="content-console-sidebar">
        <div className="content-console-panel-head is-vertical">
          <div>
            <span className="content-console-panel-tag">{KIND_PANEL_TAGS[kind]}</span>
            <h2>{KIND_LABELS[kind]}</h2>
            <p>{KIND_DESCRIPTIONS[kind]}</p>
          </div>
          <div className="content-console-toolbar-stack">
            <div className="content-console-locale-tabs">
              {LOCALES.map((item) => (
                <button key={item.value} type="button" className={locale === item.value ? 'is-active' : ''} onClick={() => setLocale(item.value)}>
                  {item.label}
                </button>
              ))}
            </div>
            <input
              className="content-console-search-input"
              type="search"
              value={keyword}
              onChange={(event) => onKeywordChange(event.target.value)}
              placeholder="按标题、Slug 或分组键搜索"
            />
            <button type="button" className="content-console-secondary-button is-block" onClick={onCreateNew}>
              {KIND_CREATE_LABELS[kind]}
            </button>
          </div>
        </div>

        <div className="content-console-list-summary">
          <span>{LANGUAGE_LABELS[locale]}版本：{totalItems}</span>
          <span>{content.id ? `正在编辑 #${content.id}` : '新建模式'}</span>
        </div>

        <div className="content-console-list">
          {loading || loadingItems ? <div className="content-console-empty">加载中...</div> : null}
          {!loading && !loadingItems && items.length === 0 ? <div className="content-console-empty">当前没有可编辑内容。</div> : null}
          {!loading && !loadingItems ? items.map((item) => (
            <button key={item.id} type="button" className={`content-console-list-item${selectedId === item.id ? ' is-active' : ''}`} onClick={() => void onSelectItem(item.id)}>
              <div className="content-console-list-item-top">
                <strong>{item.title || item.slug || item.group_key}</strong>
                <span className={`content-console-status-badge${item.is_published ? ' is-published' : ' is-draft'}`}>
                  {item.is_published ? '已发布' : '草稿'}
                </span>
              </div>
              <span>分组键：{item.group_key}</span>
              <small>Slug: {item.slug}</small>
              <small>可用内容：{item.available_languages.map((entry) => LANGUAGE_LABELS[entry]).join(' / ')}</small>
            </button>
          )) : null}
        </div>

        {!loading && !loadingItems ? <ConsolePagination currentPage={currentPage} totalPages={totalPages} onChange={onPageChange} /> : null}
      </aside>

      <main className="content-console-editor">
        <div className="content-console-panel-head">
          <div>
            <span className="content-console-panel-tag">{content.id ? '编辑版本' : '新建版本'}</span>
            <h2>{content.title || content.slug || content.group_key || `新建${KIND_LABELS[kind]}`}</h2>
            <p>保存后将刷新当前列表。</p>
          </div>
          <div className="content-console-editor-actions">
            {content.id ? <button type="button" className="content-console-danger-button" onClick={() => void onDelete()} disabled={saving}>删除</button> : null}
            <button type="button" className="content-console-secondary-button" onClick={onCreateNew}>重置</button>
            <button type="button" className="content-console-primary-button" onClick={() => void onSave()} disabled={saving}>{saving ? '保存中...' : '保存'}</button>
          </div>
        </div>

        <div className="content-console-tabs content-console-editor-mode-tabs">
          <button type="button" className={editorView === 'basic' ? 'is-active' : ''} onClick={() => setEditorView('basic')}>基础信息</button>
          {kind === 'career' ? <button type="button" className={editorView === 'requirements' ? 'is-active' : ''} onClick={() => setEditorView('requirements')}>岗位要求</button> : null}
          {kind === 'project-cooperation' ? <button type="button" className={editorView === 'requirements' ? 'is-active' : ''} onClick={() => setEditorView('requirements')}>合作模块</button> : null}
          <button type="button" className={editorView === 'body' ? 'is-active' : ''} onClick={() => setEditorView('body')}>正文内容</button>
        </div>

        <div className="content-console-editor-shell">
          <div className="content-console-editor-main">
            {editorView === 'basic' ? (
              <>
                <section className="content-console-section-card">
                  <div className="content-console-section-head is-inline-head">
                    <h3>基础信息</h3>
                    <p>分组键用于关联同一条内容，Slug 用于前台访问；置顶内容按优先级排序，数值越小越靠前。</p>
                  </div>
                  <div className="content-console-form-grid is-common">
                    <label>
                      <span>分组键</span>
                      <input value={content.group_key} onChange={(event) => setContent((current) => ({ ...current, group_key: event.target.value }))} />
                    </label>
                    <label>
                      <span>语言</span>
                      <input value={LANGUAGE_LABELS[locale]} disabled readOnly />
                    </label>
                    <label>
                      <span>Slug</span>
                      <input value={content.slug} onChange={(event) => setContent((current) => ({ ...current, slug: event.target.value }))} />
                    </label>
                    <label>
                      <span>发布日期</span>
                      <input type="date" value={content.published_at} onChange={(event) => setContent((current) => ({ ...current, published_at: event.target.value }))} />
                    </label>
                    <label>
                      <span>优先级 / 排序值</span>
                      <input type="number" value={content.sort_order} onChange={(event) => setContent((current) => ({ ...current, sort_order: Number(event.target.value) }))} />
                      <small>数值越小，展示优先级越高。</small>
                    </label>
                    <label className="content-console-checkbox">
                      <span>发布状态</span>
                      <div className="content-console-checkbox-row">
                        <input type="checkbox" checked={content.is_published} onChange={(event) => setContent((current) => ({ ...current, is_published: event.target.checked }))} />
                        <span>发布到前台</span>
                      </div>
                    </label>
                    <label className="content-console-checkbox">
                      <span>置顶</span>
                      <div className="content-console-checkbox-row">
                        <input type="checkbox" checked={content.is_pinned} onChange={(event) => setContent((current) => ({ ...current, is_pinned: event.target.checked }))} />
                        <span>列表优先展示，多个置顶按优先级 / 排序值升序排列</span>
                      </div>
                    </label>
                    {kind === 'career' || kind === 'project-cooperation' ? (
                      <label className="content-console-checkbox">
                        <span>{kind === 'career' ? '\u9ed8\u8ba4\u804c\u4f4d' : '\u9ed8\u8ba4\u5408\u4f5c\u9879\u76ee'}</span>
                        <div className="content-console-checkbox-row">
                          <input type="checkbox" checked={content.is_default_display} onChange={(event) => setContent((current) => ({ ...current, is_default_display: event.target.checked }))} />
                          <span>{kind === 'career' ? '\u8fdb\u5165\u62db\u8058\u9875\u65f6\u9ed8\u8ba4\u5c55\u793a\u6b64\u804c\u4f4d' : '\u8fdb\u5165\u9879\u76ee\u5408\u4f5c\u9875\u65f6\u9ed8\u8ba4\u5c55\u793a\u6b64\u5408\u4f5c\u9879\u76ee'}</span>
                        </div>
                      </label>
                    ) : null}
                  </div>
                </section>

                {kind === 'career' ? (
                  <>
                    <section className="content-console-section-card">
                      <div className="content-console-section-head is-inline-head">
                        <h3>联系方式</h3>
                        <p>请在此维护联系方式及二维码说明。</p>
                      </div>
                      <div className="content-console-form-grid is-two-column">
                        <label>
                          <span>电话</span>
                          <input value={content.contact_phone} onChange={(event) => setContent((current) => ({ ...current, contact_phone: event.target.value }))} />
                        </label>
                        <label>
                          <span>邮箱</span>
                          <input value={content.contact_email} onChange={(event) => setContent((current) => ({ ...current, contact_email: event.target.value }))} />
                        </label>
                      </div>
                      <div className="content-console-form-grid is-single-column content-console-tight-grid">
                        <label>
                          <span>二维码提示语</span>
                          <input value={content.qr_hint} onChange={(event) => setContent((current) => ({ ...current, qr_hint: event.target.value }))} />
                        </label>
                      </div>
                    </section>

                    <section className="content-console-section-card">
                      <div className="content-console-section-head is-inline-head">
                        <h3>二维码图片</h3>
                        <p>请上传对应的二维码图片。</p>
                      </div>
                      <div className="content-console-cover-panel">
                        <div className="content-console-cover-preview is-qr-preview">{previewUrl ? <img src={previewUrl} alt="二维码预览" /> : <span>暂无二维码图片</span>}</div>
                        <div className="content-console-cover-actions">
                          <label>
                            <span>上传二维码</span>
                            <input type="file" accept="image/*" onChange={(event) => openCropper('qr', event.target.files?.[0] ?? null, event.target)} />
                          </label>
                          {content.qr_image_url && !content.qrImageFile ? <button type="button" className="content-console-secondary-button" onClick={() => setContent((current) => ({ ...current, clear_qr_image: !current.clear_qr_image }))}>{content.clear_qr_image ? '撤销移除二维码' : '移除现有二维码'}</button> : null}
                        </div>
                      </div>
                    </section>
                  </>
                ) : kind === 'project-cooperation' ? (
                  <>
                    <section className="content-console-section-card">
                      <div className="content-console-section-head is-inline-head">
                        <h3>问卷信息</h3>
                        <p>请在此维护二维码问卷标题、说明和提示语。</p>
                      </div>
                      <div className="content-console-form-grid is-single-column">
                        <label>
                          <span>问卷标题</span>
                          <input value={content.questionnaire_title} onChange={(event) => setContent((current) => ({ ...current, questionnaire_title: event.target.value }))} />
                        </label>
                        <label>
                          <span>问卷说明</span>
                          <textarea rows={4} value={content.questionnaire_description} onChange={(event) => setContent((current) => ({ ...current, questionnaire_description: event.target.value }))} />
                        </label>
                        <label>
                          <span>二维码提示语</span>
                          <input value={content.qr_hint} onChange={(event) => setContent((current) => ({ ...current, qr_hint: event.target.value }))} />
                        </label>
                      </div>
                    </section>

                    <section className="content-console-section-card">
                      <div className="content-console-section-head is-inline-head">
                        <h3>问卷二维码</h3>
                        <p>请上传项目合作页展示的问卷二维码图片。</p>
                      </div>
                      <div className="content-console-cover-panel">
                        <div className="content-console-cover-preview is-qr-preview">{previewUrl ? <img src={previewUrl} alt="问卷二维码预览" /> : <span>暂无二维码图片</span>}</div>
                        <div className="content-console-cover-actions">
                          <label>
                            <span>上传二维码</span>
                            <input type="file" accept="image/*" onChange={(event) => openCropper('qr', event.target.files?.[0] ?? null, event.target)} />
                          </label>
                          {content.qr_image_url && !content.qrImageFile ? <button type="button" className="content-console-secondary-button" onClick={() => setContent((current) => ({ ...current, clear_qr_image: !current.clear_qr_image }))}>{content.clear_qr_image ? '撤销移除二维码' : '移除现有二维码'}</button> : null}
                        </div>
                      </div>
                    </section>
                  </>
                ) : (
                  <>
                    <section className="content-console-section-card">
                      <div className="content-console-section-head is-inline-head">
                        <h3>{kind === 'news' ? '主题' : '分类'}</h3>
                        <p>{kind === 'news' ? '可为当前内容配置一个或多个新闻主题。' : '请选择资源分类；服务案例可额外绑定到定制化服务页展示位。'}</p>
                      </div>
                      {kind === 'news' ? (
                        <label className="is-full">
                          <span>新闻主题</span>
                          <div className="content-console-check-grid">
                            {NEWS_TOPICS.map((topic) => (
                              <label key={topic.value} className="content-console-inline-check">
                                <input
                                  type="checkbox"
                                  checked={content.news_topics.includes(topic.value)}
                                  onChange={(event) => setContent((current) => ({
                                    ...current,
                                    news_topics: event.target.checked ? [...current.news_topics, topic.value] : current.news_topics.filter((entry) => entry !== topic.value),
                                  }))}
                                />
                                <span>{topic.label}</span>
                              </label>
                            ))}
                          </div>
                        </label>
                      ) : (
                        <div className="content-console-form-grid is-two-column">
                          <label>
                            <span>资源分类</span>
                            <select value={content.resource_category} onChange={(event) => setContent((current) => ({ ...current, resource_category: event.target.value as ContentForm['resource_category'], service_page_slot: event.target.value === 'service-cases' ? current.service_page_slot : '' }))}>
                              {RESOURCE_CATEGORIES.map((category) => <option key={category.value} value={category.value}>{category.label}</option>)}
                            </select>
                          </label>
                          {content.resource_category === 'service-cases' ? (
                            <label>
                              <span>服务页展示位</span>
                              <select value={content.service_page_slot} onChange={(event) => setContent((current) => ({ ...current, service_page_slot: event.target.value }))}>
                                {SERVICE_PAGE_SLOT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                              </select>
                            </label>
                          ) : null}
                        </div>
                      )}
                    </section>

                    <section className="content-console-section-card">
                      <div className="content-console-section-head is-inline-head">
                        <h3>封面图片</h3>
                        <p>请在此维护封面图片。</p>
                      </div>
                      <div className="content-console-cover-panel">
                        <div className="content-console-cover-preview">{previewUrl ? <img src={previewUrl} alt="封面预览" /> : <span>暂无封面图片</span>}</div>
                        <div className="content-console-cover-actions">
                          <label>
                            <span>上传封面</span>
                            <input type="file" accept="image/*" onChange={(event) => openCropper('cover', event.target.files?.[0] ?? null, event.target)} />
                          </label>
                          {content.cover_image_url && !content.coverFile ? <button type="button" className="content-console-secondary-button" onClick={() => setContent((current) => ({ ...current, clear_cover_image: !current.clear_cover_image }))}>{content.clear_cover_image ? '撤销移除封面' : '移除现有封面'}</button> : null}
                        </div>
                      </div>
                    </section>
                  </>
                )}
              </>
            ) : editorView === 'requirements' && kind === 'career' ? (
              renderCareerRequirements()
            ) : editorView === 'requirements' && kind === 'project-cooperation' ? (
              renderCooperationItems()
            ) : (
              <section className="content-console-section-card">
                <div className="content-console-section-head is-inline-head">
                  <h3>正文内容</h3>
                  <p>请在此维护正文内容。</p>
                </div>
                <div className="content-console-locale-card">
                  <div className="content-console-form-grid is-two-column">
                    <label>
                      <span>标签</span>
                      <input value={content.badge} onChange={(event) => setContent((current) => ({ ...current, badge: event.target.value }))} />
                    </label>
                    <label>
                      <span>标题</span>
                      <input value={content.title} onChange={(event) => setContent((current) => ({ ...current, title: event.target.value }))} />
                    </label>
                  </div>
                  <div className="content-console-form-grid is-single-column">
                    <label>
                      <span>摘要</span>
                      <textarea rows={4} value={content.summary} onChange={(event) => setContent((current) => ({ ...current, summary: event.target.value }))} />
                    </label>
                    <label>
                      <span>正文</span>
                      <textarea rows={kind === 'career' ? 12 : 14} value={content.body} onChange={(event) => setContent((current) => ({ ...current, body: event.target.value }))} />
                    </label>
                  </div>
                </div>
              </section>
            )}
          </div>

          <aside className="content-console-detail-sidebar">
            <section className="content-console-section-card is-sidebar-card">
              <div className="content-console-section-head is-inline-head">
                <h3>版本信息</h3>
                <p>用于查看当前记录的基础状态信息。</p>
              </div>
              <dl className="content-console-meta-list">
                <div><dt>ID</dt><dd>{content.id ?? '未创建'}</dd></div>
                <div><dt>语言</dt><dd>{LANGUAGE_LABELS[locale]}</dd></div>
                <div><dt>分组键</dt><dd>{content.group_key || '--'}</dd></div>
                <div><dt>状态</dt><dd>{content.is_published ? '已发布' : '草稿'}</dd></div>
                <div><dt>置顶</dt><dd>{content.is_pinned ? '是' : '否'}</dd></div>
                <div><dt>优先级 / 排序值</dt><dd>{content.sort_order}</dd></div>
                {kind === 'career' || kind === 'project-cooperation' ? <div><dt>{'\u9ed8\u8ba4\u5c55\u793a'}</dt><dd>{content.is_default_display ? '\u662f' : '\u5426'}</dd></div> : null}
                {kind === 'resource' && content.resource_category === 'service-cases' ? <div><dt>服务页展示位</dt><dd>{SERVICE_PAGE_SLOT_OPTIONS.find((option) => option.value === content.service_page_slot)?.label ?? '未绑定'}</dd></div> : null}
                {kind === 'project-cooperation' ? <div><dt>合作模块数</dt><dd>{content.cooperation_items.length}</dd></div> : null}
                {kind === 'project-cooperation' ? <div><dt>问卷标题</dt><dd>{content.questionnaire_title || '--'}</dd></div> : null}
                <div><dt>{mediaLabel}</dt><dd>{previewUrl ? `已上传${mediaLabel}` : `未上传${mediaLabel}`}</dd></div>
              </dl>
            </section>

            <section className="content-console-section-card is-sidebar-card">
              <div className="content-console-section-head is-inline-head">
                <h3>内容概览</h3>
                <p>显示当前分组下已建立的记录。</p>
              </div>
              <div className="content-console-permission-grid is-single-column-grid">
                {content.available_languages.map((entry) => (
                  <div key={entry} className="content-console-permission-chip is-enabled">
                    <strong>{LANGUAGE_LABELS[entry]}</strong>
                    <span>{entry === locale ? '当前记录' : '已建立'}</span>
                  </div>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default ContentWorkspace;



